import {
  useActionData,
  useFetcher,
  useLocation,
  useNavigate,
  useNavigation,
  useRevalidator,
  useRouteLoaderData,
} from "@remix-run/react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import BookingCartSidebar from "~/components/booking/BookingCartSidebar";
import CheckoutStep from "~/components/booking/flow/CheckoutStep";
import DaySelect from "~/components/booking/flow/DaySelect";
import GuestsSelect from "~/components/booking/flow/GuestsSelect";
import PackageStep from "~/components/booking/flow/PackageStep";
import PaymentStep from "~/components/booking/flow/PaymentStep";
import ServiceCategoryStep from "~/components/booking/flow/ServiceCategoryStep";
import ServiceDurationStep from "~/components/booking/flow/ServiceDurationStep";
import ServiceTypeStep from "~/components/booking/flow/ServiceTypeStep";
import TimeSelect from "~/components/booking/flow/TimeSelect";
import UpsellingStep from "~/components/booking/flow/UpsellingStep";
import { GlobalLoader } from "~/components/common/GlobalLoader";
import ChevronRight from "~/components/icons/ChevronRight";
import { Button } from "~/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { trackAddToCart } from "~/lib/facebook-pixel";
import { mapBookingCartItemToGA4Item, trackGA4AddToCart } from "~/lib/gtm-datalayer";
import { keepAnimationOnMainThread } from "~/lib/motion";
import {
  areDurationVariants,
  pickLongestTreatmentPackage,
  VOUCHER_PACKAGE_ALTERNATIVES_SELECTABLE,
} from "~/lib/booking-voucher-packages";
import { cn, getCmsServiceCategoryFromList } from "~/lib/utils";
import { servicesCoveredByAllVouchers } from "~/lib/voucher-combination";
import { useAppStore } from "~/stores/app";
import {
  BookingCartProvider,
  useBookingCartStoreFromContext,
} from "~/stores/bookingCartContext";

const NUMBER_OF_STEPS = 9;

interface BookingPageProps {
  isVoucherBooking?: boolean;
  backUrl?: string;
}

export default function BookingPage(props: BookingPageProps) {
  // Avvolgiamo l'intero sottoalbero del flusso con il provider che decide
  // quale Zustand store usare (normale vs voucher), in modo che due tab
  // aperti in parallelo non si sovrascrivano il cart in localStorage.
  return (
    <BookingCartProvider isVoucherBooking={!!props.isVoucherBooking}>
      <BookingPageInner {...props} />
    </BookingCartProvider>
  );
}

function BookingPageInner({
  isVoucherBooking,
  backUrl,
}: BookingPageProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigation = useNavigation();
  const navigate = useNavigate();
  const rootData = useRouteLoaderData<any>("root");
  const revalidator = useRevalidator();
  const token = rootData?.token;
  const prevLocationRef = useRef<string>(location.pathname);
  const hasResetGuestsOnMountRef = useRef(false);
  const shouldBlockNavigationRef = useRef(false);
  // Set when the payment step is about to hand the customer off to an external
  // payment provider (Scalapay's hosted checkout, or a Stripe 3DS challenge).
  // That navigation is intentional and expected, so none of the four
  // navigation guards below should challenge it. Without this the customer got
  // the browser's native "are you sure you want to leave this page?" dialog on
  // the way out, which is nonsense: leaving IS the flow, and the prompt invites
  // them to cancel their own payment.
  const leavingForPaymentProviderRef = useRef(false);
  const { openAuthModal, authModalFrom, authModalOpened } = useAppStore();
  const [guestsCount, setGuestsCount] = useState({ male: 0, female: 0 });
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedAvailability, setSelectedAvailability] = useState<any>();
  const [selectedCmsService, setSelectedCmsService] = useState<any>();
  const [selectedApiService, setSelectedApiService] = useState<any>();
  // Selezione (pre-conferma) dello step "Seleziona il servizio". Tenuta qui nel
  // genitore così sopravvive a un eventuale rimonta di ServiceTypeStep, che
  // altrimenti perderebbe la selezione locale (l'anello sparirebbe).
  const [serviceTypeSelection, setServiceTypeSelection] = useState<{
    cmsService: any;
    apiService: any;
  } | null>(null);
  const [selectedService, setSelectedService] = useState<any>();
  const [selectedTime, setSelectedTime] = useState<any>();
  const [selectedPackage, setSelectedPackage] = useState<any>();
  const [selectedPackages, setSelectedPackages] = useState<any[]>([]);
  const [packageData, setPackageData] = useState<any>();
  const [selectedUpsellingService, setSelectedUpsellingService] =
    useState<any>();
  const [selectedUpsellingTime, setSelectedUpsellingTime] = useState<any[]>([]);
  const [selectedUpsellingQuantity, setSelectedUpsellingQuantity] = useState(
    () => guestsCount.male + guestsCount.female,
  );
  const [loadingAddToCart, setLoadingAddToCart] = useState<any>(false);
  const [upsellingLoading, setUpsellingLoading] = useState(false);
  const [summary, setSummary] = useState<any>({ total: 0, totalGuests: 0 });
  const [pendingUpsellingCart, setPendingUpsellingCart] = useState<any>(null);
  const [pendingCartBeforeSignup, setPendingCartBeforeSignup] =
    useState<any>(null);
  const prevStepRef = useRef(0);
  const actionData = useActionData<any>();
  const stepKeyRef = useRef(0);
  const userDeselectedAllUpsellingRef = useRef<boolean>(false);

  const [currentStep, setCurrentStep] = useState(0);
  const [previousStep, setPreviousStep] = useState(0);
  const [direction, setDirection] = useState(0);
  const [services, setServices] = useState<any[]>([]);
  const [skippedSteps, setSkippedSteps] = useState<number[]>([]);
  const [hasAddToCartErrors, setHasAddToCartErrors] = useState<
    boolean | { error: any }
  >(false);
  const [availabilitiesForDate, setAvailabilitiesForDate] = useState<
    any[] | undefined
  >();
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [showUpsellingBackConfirm, setShowUpsellingBackConfirm] =
    useState(false);
  const [pendingStepChange, setPendingStepChange] = useState<number | null>(
    null,
  );
  const [hasAddedToCartInThisFlow, setHasAddedToCartInThisFlow] =
    useState(false);
  // Risolvo lo store giusto dal context (normale o voucher) e poi seleziono.
  const useBookingCartStore = useBookingCartStoreFromContext();
  const { mobileCartOpen: storeMobileCartOpen, setMobileCartOpen } =
    useBookingCartStore();
  const [mobileCartOpen, setMobileCartOpenLocal] = useState(false);

  useEffect(() => {
    setMobileCartOpenLocal(storeMobileCartOpen);
  }, [storeMobileCartOpen]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  const [shouldAnimateCart, setShouldAnimateCart] = useState(false);
  const [isInitializingSession, setIsInitializingSession] = useState(false);
  const prevCartItemsCountRef = useRef<number>(0);

  const lastProcessedAddToCartUpsellingDataRef = useRef<any>(null);
  const lastProcessedRemoveFromCartDataRef = useRef<any>(null);
  const initSessionAfterModalCloseRef = useRef(false);
  const wasBookingModalRef = useRef(false);
  const initSessionCalledRef = useRef(false);
  const cartClearedOnMountRef = useRef(false);
  const savedCustomDataRef = useRef<{
    customReferentData?: any;
    customGuestsData?: any;
  } | null>(null);
  const lastInitSessionTimeRef = useRef<number | null>(null);
  const isRestoringCartRef = useRef(false);
  const isInitializingSessionRef = useRef(false);
  const hasProcessedSignupCartRef = useRef(false);

  const {
    bookingCart,
    setBookingCart,
    loading: bookingCartLoading,
    hasSignupCustomerId,
    setHasSignupCustomerId,
    clearCart,
    clearVouchers,
  } = useBookingCartStore();

  const hasVouchers = bookingCart?.vouchers && bookingCart.vouchers.length > 0;
  const isVoucherBookingWithoutVouchers = isVoucherBooking && !hasVouchers;
  const addToCartUpsellingFetcher = useFetcher<any>();
  const removeFromCartFetcher = useFetcher<any>();

  const resetBookingToStep0 = useCallback(() => {
    setPreviousStep(0);
    stepKeyRef.current += 1;
    setBookingCart(null);
    setCurrentStep(0);
    setSelectedDate(undefined);
    setSelectedAvailability(undefined);
    setSelectedCmsService(undefined);
    setSelectedApiService(undefined);
    setSelectedService(undefined);
    setSelectedTime(undefined);
    setSelectedPackage(undefined);
    setSelectedPackages([]);
    setPackageData(undefined);
    setSelectedUpsellingService(undefined);
    setSelectedUpsellingTime([]);
    setSelectedUpsellingQuantity(guestsCount.male + guestsCount.female);
    setLoadingAddToCart(false);
    setHasAddToCartErrors(false);
    setHasAddedToCartInThisFlow(false);
    hasProcessedSignupCartRef.current = false;
  }, [setBookingCart, guestsCount.male, guestsCount.female]);

  const initSession = useCallback(async (): Promise<Response | null> => {
    if (isInitializingSessionRef.current) {
      return null;
    }
    isInitializingSessionRef.current = true;
    setIsInitializingSession(true);
    try {
      const response = await fetch(
        `/api/auth/init-session${
          isVoucherBooking ? "?isVoucherBooking=true" : ""
        }`,
      );
      lastInitSessionTimeRef.current = Date.now();
      if (typeof window !== "undefined") {
        localStorage.setItem("last-init-session-time", Date.now().toString());
      }
      return response;
    } catch (error) {
      console.error("Failed to init session:", error);
      return null;
    } finally {
      isInitializingSessionRef.current = false;
      setIsInitializingSession(false);
    }
  }, []);

  const ensureSessionBeforeAddToCart = useCallback(async () => {
    const now = Date.now();
    const lastInitTime = lastInitSessionTimeRef.current || 0;
    const tenMinutesInMs = 10 * 60 * 1000;

    if (now - lastInitTime > tenMinutesInMs) {
      const initResponse = await initSession();
      if (initResponse && initResponse.ok) {
        lastInitSessionTimeRef.current = Date.now();
        if (typeof window !== "undefined") {
          localStorage.setItem("last-init-session-time", Date.now().toString());
        }
        return true;
      }
      return false;
    }
    return true;
  }, [initSession]);

  const performAddToCart = useCallback(
    async (cartData: any, actionUrl?: string) => {
      const action =
        actionUrl ||
        `/api/booking/add-to-cart${
          isVoucherBooking ? "?isVoucherBooking=true" : ""
        }`;
      setLoadingAddToCart(true);
      setHasAddToCartErrors(false);

      try {
        const response = await fetch(action, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(cartData),
        });
        const data: any = await response.json();

        if (data?.Success && data.Response) {
          if (!isRestoringCartRef.current) {
            setHasAddedToCartInThisFlow(true);
          }

          const savedCustomData = savedCustomDataRef.current;
          const currentCustomData =
            bookingCart?.customReferentData || bookingCart?.customGuestsData
              ? {
                  customReferentData: bookingCart.customReferentData,
                  customGuestsData: bookingCart.customGuestsData,
                }
              : null;
          const responseCart = data.Response;
          const customDataToUse = savedCustomData || currentCustomData;

          if (
            customDataToUse &&
            (customDataToUse.customReferentData ||
              customDataToUse.customGuestsData)
          ) {
            setBookingCart({
              ...responseCart,
              customReferentData:
                customDataToUse.customReferentData ||
                responseCart.customReferentData,
              customGuestsData:
                customDataToUse.customGuestsData ||
                responseCart.customGuestsData,
            });
            savedCustomDataRef.current = null;
          } else {
            setBookingCart(responseCart);
          }

          if (!isRestoringCartRef.current && responseCart?.BasketItems) {
            const mainItems = responseCart.BasketItems.filter(
              (item: any) => item.Role === "MAIN",
            );
            if (mainItems.length > 0) {
              try {
                const lastItem = mainItems[mainItems.length - 1];
                const itemPrice =
                  lastItem.GrossAmount?.Price ||
                  lastItem.GrossPrice?.Price ||
                  0;
                trackAddToCart({
                  content_name: lastItem.Service || "Servizio",
                  content_ids: [lastItem.Service || ""],
                  currency:
                    lastItem.GrossAmount?.Currency ||
                    lastItem.GrossPrice?.Currency ||
                    "EUR",
                  value: itemPrice,
                  num_items: lastItem.Quantity?.Total || 1,
                });

                trackGA4AddToCart(
                  [mapBookingCartItemToGA4Item(lastItem)],
                  itemPrice,
                  lastItem.GrossAmount?.Currency ||
                    lastItem.GrossPrice?.Currency ||
                    "EUR",
                );
              } catch (error) {
                console.error("Error tracking AddToCart:", error);
              }
            }
          }

          if (!isRestoringCartRef.current) {
            if (currentStep === 5) {
              setCurrentStep(6);
            } else if (currentStep === 4.5) {
              setCurrentStep(6);
            }
          }

          return data;
        }

        if (data?.error) {
          setHasAddToCartErrors({ error: data.error });
          console.error("ADD TO CART ERROR", data);
        }

        return data;
      } catch (error) {
        console.error("ADD TO CART ERROR", error);
        setHasAddToCartErrors({ error: "Failed to add to cart" });
        return null;
      } finally {
        setLoadingAddToCart(false);
      }
    },
    [bookingCart, currentStep, isVoucherBooking, setBookingCart],
  );

  const getPreviousVisibleStep = useCallback(
    (fromStep: number): number => {
      if (fromStep === 6 && selectedPackage) {
        return 4.5;
      }

      let targetStep = fromStep - 1;
      if (fromStep === 4.5) {
        targetStep = 3;
      }

      while (targetStep >= 0 && skippedSteps.includes(targetStep)) {
        targetStep--;
      }
      return Math.max(0, targetStep);
    },
    [skippedSteps, selectedPackage],
  );

  const handleBack = useCallback(() => {
    if (isVoucherBookingWithoutVouchers) return;
    if (currentStep > 0) {
      if (currentStep === 6) {
        const targetStep = getPreviousVisibleStep(currentStep);
        setSkippedSteps((prev) => prev.filter((s) => s < targetStep));
        setPendingStepChange(targetStep);
        setShowUpsellingBackConfirm(true);
      } else {
        const targetStep = getPreviousVisibleStep(currentStep);
        if (targetStep <= 3) {
          hasAutoSkippedStep4Ref.current = false;
        }
        setSkippedSteps((prev) => prev.filter((s) => s < targetStep));
        setCurrentStep(targetStep);
      }
    }
  }, [currentStep, getPreviousVisibleStep, isVoucherBookingWithoutVouchers]);

  const goToStep7 = useCallback(() => {
    setCurrentStep(7);
  }, []);

  const confirmUpsellingBackNavigation = useCallback(() => {
    if (pendingStepChange !== null) {
      clearCart();
      setSelectedUpsellingService(undefined);
      setSelectedUpsellingTime([]);
      setSelectedUpsellingQuantity(guestsCount.male + guestsCount.female);
      setHasAddedToCartInThisFlow(false);
      // Tornando a uno step pre-pacchetto (≤ 4) dobbiamo resettare anche
      // la selezione del pacchetto/servizio, altrimenti gli step di
      // selezione mostrano dati stantii del pacchetto vecchio (varianti,
      // orari del pacchetto, ecc.) — "selezione che non c'entra nulla".
      if (pendingStepChange < 4.5) {
        setSelectedPackage(undefined);
        setSelectedPackages([]);
        setSelectedService(undefined);
        setSelectedApiService(undefined);
        setSelectedTime(undefined);
        hasAutoSkippedStep4Ref.current = false;
        // Se torniamo prima dello step 3 (ServiceType) resettiamo anche la
        // selezione del tipo di servizio per ricostruire la lista pacchetti
        // / varianti corretta a partire dalla nuova navigazione.
        if (pendingStepChange < 3) {
          setSelectedCmsService(undefined);
        }
      }
      // Rimuoviamo gli skippedSteps QUI (solo dopo conferma), non quando
      // viene aperta la modale: altrimenti gli step rimossi tornano visibili
      // nello stepper mentre l'utente sta ancora valutando.
      setSkippedSteps((prev) => prev.filter((s) => s < pendingStepChange));
      setCurrentStep(pendingStepChange);
      setPendingStepChange(null);
    }
    setShowUpsellingBackConfirm(false);
  }, [pendingStepChange, clearCart, guestsCount]);

  const cancelUpsellingBackNavigation = useCallback(() => {
    setPendingStepChange(null);
    setShowUpsellingBackConfirm(false);
  }, []);

  const getSummary = useCallback(() => {
    if (
      !bookingCart ||
      !bookingCart.BasketItems ||
      bookingCart.BasketItems.length === 0
    ) {
      return { total: 0, currency: "", totalGuests: 0 };
    }

    const total = bookingCart.BasketItems.reduce(
      (acc, item) => acc + (item.GrossAmount?.Price || 0),
      0,
    );
    const totalGuests = bookingCart.BasketItems.reduce(
      (acc, item) => acc + (item.Quantity?.Total || 0),
      0,
    );

    return { total, totalGuests };
  }, [bookingCart]);

  useEffect(() => {
    setSummary(getSummary());
  }, [bookingCart]);

  useEffect(() => {
    if (
      bookingCart &&
      bookingCart.BasketItems &&
      bookingCart.BasketItems.length > 0
    ) {
      const firstItem = bookingCart.BasketItems[0];

      if (firstItem.CheckinDate) {
        const checkinDate = new Date(firstItem.CheckinDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (checkinDate >= today && !selectedDate) {
          setSelectedDate(checkinDate);
        }
      }
    }
  }, [bookingCart]);

  useEffect(() => {
    if (isVoucherBookingWithoutVouchers && currentStep > 0) {
      setCurrentStep(0);
      return;
    }
    if (currentStep === 0 && previousStep > 0) {
      clearCart();
      setSelectedDate(undefined);
    }
    if (previousStep === 0 && currentStep > 0) {
      setDirection(-1);
      setPreviousStep(currentStep);
    }
    if (previousStep < currentStep) {
      setDirection(1);
      setPreviousStep(currentStep);
    }
    setLoadingAddToCart(false);
  }, [currentStep, previousStep, clearCart, isVoucherBookingWithoutVouchers]);

  useEffect(() => {
    if (mobileCartOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileCartOpen]);

  useEffect(() => {
    const currentItemsCount =
      bookingCart?.BasketItems?.filter((item) => item.Role !== "INCLUDED")
        .length || 0;

    if (currentItemsCount !== prevCartItemsCountRef.current) {
      setShouldAnimateCart(true);
      setTimeout(() => setShouldAnimateCart(false), 600);
    }

    prevCartItemsCountRef.current = currentItemsCount;
  }, [bookingCart]);

  useEffect(() => {
    if (!initSessionCalledRef.current) {
      initSessionCalledRef.current = true;
      initSession();
    }
  }, [initSession]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const storedTime = localStorage.getItem("last-init-session-time");
    if (storedTime) {
      lastInitSessionTimeRef.current = parseInt(storedTime, 10);
    }

    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        const now = Date.now();
        const lastInitTime = lastInitSessionTimeRef.current || 0;
        const tenMinutesInMs = 10 * 60 * 1000;

        if (now - lastInitTime > tenMinutesInMs) {
          const currentCart: any = bookingCart;
          const cartToRestore =
            currentCart?.BasketItems?.length &&
            currentCart?.BasketItems?.length > 0
              ? {
                  ...currentCart,
                }
              : null;

          try {
            const initResponse = await initSession();
            if (initResponse && initResponse.ok) {
              lastInitSessionTimeRef.current = Date.now();
              localStorage.setItem(
                "last-init-session-time",
                Date.now().toString(),
              );

              if (cartToRestore && cartToRestore.BasketItems.length > 0) {
                isRestoringCartRef.current = true;
                setLoadingAddToCart(true);
                try {
                  const cartResponse = await fetch(
                    `/api/booking/cart${
                      isVoucherBooking ? "?isVoucherBooking=true" : ""
                    }`,
                  );
                  const cartData = await cartResponse.json();

                  const savedItemsCount = cartToRestore.BasketItems.length;
                  const retrievedItemsCount =
                    (cartData as any)?.BasketItems?.length || 0;

                  if (
                    retrievedItemsCount === 0 ||
                    retrievedItemsCount !== savedItemsCount
                  ) {
                    await ensureSessionBeforeAddToCart();
                    await performAddToCart(cartToRestore);
                  } else {
                    const restoredCart = {
                      ...(cartData as any),
                      customReferentData: currentCart?.customReferentData,
                      customGuestsData: currentCart?.customGuestsData,
                      customTaxCode: currentCart?.customTaxCode,
                      customVatId: currentCart?.customVatId,
                      customTaxCodeOrVAT: currentCart?.customTaxCodeOrVAT,
                      DiscountCodes: currentCart?.DiscountCodes,
                    };
                    setBookingCart(restoredCart);
                    setLoadingAddToCart(false);
                  }
                } catch (error) {
                  console.error("Failed to restore cart:", error);
                  if (cartToRestore) {
                    await ensureSessionBeforeAddToCart();
                    await performAddToCart(cartToRestore);
                  }
                } finally {
                  isRestoringCartRef.current = false;
                }
              }
            }
          } catch (error) {
            console.error(
              "Failed to init session on visibility change:",
              error,
            );
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    bookingCart,
    setBookingCart,
    performAddToCart,
    ensureSessionBeforeAddToCart,
  ]);

  useEffect(() => {
    if (currentStep === 0 && !cartClearedOnMountRef.current) {
      cartClearedOnMountRef.current = true;

      const clearCartOnStart = async () => {
        clearCart();
        // localStorage è condiviso tra tab: se un altro tab è nel flusso
        // voucher, potrebbe aver lasciato `bookingCart.vouchers` qui.
        // In modalità non-voucher quei vouchers fantasma deviano la logica
        // (filtri servizi, auto-skip step). Li rimuoviamo all'avvio.
        if (!isVoucherBooking) {
          clearVouchers();
        }

        try {
          const deleteResponse = await fetch(
            `/api/booking/delete-basket${
              isVoucherBooking ? "?isVoucherBooking=true" : ""
            }`,
            {
              method: "DELETE",
            },
          );

          if (!deleteResponse.ok && deleteResponse.status !== 403) {
            console.error(
              "Failed to delete basket:",
              deleteResponse.statusText,
            );
          }
        } catch (error) {
          console.error("Failed to clear cart:", error);
        }
      };

      clearCartOnStart();
    }
  }, [currentStep, clearCart, clearVouchers, isVoucherBooking]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!shouldBlockNavigationRef.current) {
        return;
      }
      if (currentStep > 0) {
        e.preventDefault();
        e.returnValue = t(
          "Sei sicuro di voler lasciare questa pagina? I tuoi dati potrebbero andare persi.",
        );
        return t(
          "Sei sicuro di voler lasciare questa pagina? I tuoi dati potrebbero andare persi.",
        );
      }
    };

    const handlePopState = (e: PopStateEvent) => {
      if (!shouldBlockNavigationRef.current) {
        return;
      }
      if (currentStep > 0) {
        const confirmLeave = window.confirm(
          t(
            "Sei sicuro di voler lasciare questa pagina? I tuoi dati potrebbero andare persi.",
          ),
        );
        if (!confirmLeave) {
          window.history.pushState(null, "", window.location.href);
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    if (currentStep > 0) {
      window.history.pushState(null, "", window.location.href);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [currentStep, t]);

  useEffect(() => {
    // Never re-arm the guard once we are on our way to a payment provider:
    // this effect re-runs on every currentStep change, and re-arming
    // mid-handoff would bring the dialog back.
    shouldBlockNavigationRef.current =
      !leavingForPaymentProviderRef.current && currentStep > 0;
  }, [currentStep]);

  // Handed to PaymentStep so it can disarm the guards immediately before it
  // navigates off-site. Disarms all four at once (beforeunload, popstate, the
  // Remix navigation interceptor and the link-click interceptor), because they
  // all read shouldBlockNavigationRef.
  const handleLeavingForPaymentProvider = useCallback(() => {
    leavingForPaymentProviderRef.current = true;
    shouldBlockNavigationRef.current = false;
  }, []);

  useEffect(() => {
    if (
      shouldBlockNavigationRef.current &&
      navigation.location &&
      navigation.location.pathname !== location.pathname &&
      !navigation.location.pathname.includes("/booking") &&
      !navigation.location.pathname.includes("/order/confirmed") &&
      navigation.location.pathname !== prevLocationRef.current
    ) {
      prevLocationRef.current = navigation.location.pathname;

      if (navigation.state === "loading") {
        const confirmLeave = window.confirm(
          t(
            "Sei sicuro di voler lasciare questa pagina? I tuoi dati potrebbero andare persi.",
          ),
        );

        if (!confirmLeave) {
          navigate(location.pathname, { replace: true });
          prevLocationRef.current = location.pathname;
          return;
        } else {
          shouldBlockNavigationRef.current = false;
        }
      }
    }
  }, [
    navigation.state,
    navigation.location,
    location.pathname,
    navigate,
    currentStep,
    t,
  ]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!shouldBlockNavigationRef.current) return;

      const target = e.target as HTMLElement;
      const link = target.closest("a");

      if (link && link.href) {
        const url = new URL(link.href);
        const target = link.target as string;
        if (target === "_blank") {
          return;
        }
        const currentUrl = new URL(window.location.href);

        if (
          url.pathname !== currentUrl.pathname &&
          !url.pathname.includes("/booking") &&
          !url.pathname.includes("/order/confirmed")
        ) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          const confirmLeave = window.confirm(
            t(
              "Sei sicuro di voler lasciare questa pagina? I tuoi dati potrebbero andare persi.",
            ),
          );

          if (confirmLeave) {
            shouldBlockNavigationRef.current = false;
            window.location.href = link.href;
          }
        }
      }
    };

    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, [t]);

  useEffect(() => {
    const isPackage =
      selectedApiService?.Type === "Package" ||
      selectedService?.Type === "Package" ||
      selectedPackage;

    setSkippedSteps((prev) => {
      if (isPackage) {
        return prev.includes(5) ? prev : [...prev, 5];
      } else {
        return prev.includes(5) ? prev.filter((s) => s !== 5) : prev;
      }
    });
  }, [selectedApiService, selectedService, selectedPackage]);

  useEffect(() => {
    if (
      addToCartUpsellingFetcher.state === "idle" &&
      addToCartUpsellingFetcher.data &&
      addToCartUpsellingFetcher.data !==
        lastProcessedAddToCartUpsellingDataRef.current
    ) {
      lastProcessedAddToCartUpsellingDataRef.current =
        addToCartUpsellingFetcher.data;

      if (
        addToCartUpsellingFetcher.data?.Success &&
        addToCartUpsellingFetcher.data.Response
      ) {
        setLoadingAddToCart(false);
        setHasAddedToCartInThisFlow(true);
        setBookingCart(addToCartUpsellingFetcher.data.Response);
        setSelectedUpsellingService(undefined);
        setSelectedUpsellingTime([]);
        setSelectedUpsellingQuantity(guestsCount.male + guestsCount.female);
        return;
      }

      if (addToCartUpsellingFetcher.data?.error) {
        setLoadingAddToCart(false);
        setHasAddToCartErrors({
          error: addToCartUpsellingFetcher.data?.error,
        });
        console.error("ADD TO CART UPSELLING ERROR", addToCartUpsellingFetcher);
      }
    }
  }, [
    addToCartUpsellingFetcher.state,
    addToCartUpsellingFetcher.data,
    goToStep7,
    guestsCount.male,
    guestsCount.female,
    setBookingCart,
  ]);

  useEffect(() => {
    if (
      removeFromCartFetcher.state === "idle" &&
      removeFromCartFetcher.data &&
      removeFromCartFetcher.data !== lastProcessedRemoveFromCartDataRef.current
    ) {
      lastProcessedRemoveFromCartDataRef.current = removeFromCartFetcher.data;

      const formAction = (removeFromCartFetcher as any).formAction;
      const isDeleteBasket =
        (typeof formAction === "string" &&
          formAction.includes("delete-basket")) ||
        (removeFromCartFetcher.data?.Success &&
          !removeFromCartFetcher.data?.Response);

      if (removeFromCartFetcher.data?.Success) {
        setLoadingAddToCart(false);

        if (isDeleteBasket) {
          setPreviousStep(0);
          stepKeyRef.current += 1;
          setBookingCart(null);
          setCurrentStep(0);
          setSelectedDate(undefined);
          setSelectedAvailability(undefined);
          setSelectedCmsService(undefined);
          setSelectedApiService(undefined);
          setSelectedService(undefined);
          setSelectedTime(undefined);
          setSelectedPackage(undefined);
          setSelectedPackages([]);
          setPackageData(undefined);
          setSelectedUpsellingService(undefined);
          setSelectedUpsellingTime([]);
          setSelectedUpsellingQuantity(guestsCount.male + guestsCount.female);
          setSkippedSteps([]);
          setHasAddedToCartInThisFlow(false);
          return;
        } else if (removeFromCartFetcher.data.Response) {
          const updatedCart = removeFromCartFetcher.data.Response;
          setBookingCart(updatedCart);

          const hasMainServices = updatedCart?.BasketItems?.some(
            (item: any) => item.Role === "MAIN",
          );

          if (
            !updatedCart?.BasketItems ||
            updatedCart.BasketItems.length === 0 ||
            !hasMainServices
          ) {
            setCurrentStep(0);
            setHasAddedToCartInThisFlow(false);
          }
          return;
        }
      }

      if (removeFromCartFetcher.data?.error) {
        setLoadingAddToCart(false);
        setHasAddToCartErrors({
          error: removeFromCartFetcher.data?.error,
        });
        console.error("REMOVE FROM CART ERROR", removeFromCartFetcher);
      }
    }
  }, [
    removeFromCartFetcher.state,
    removeFromCartFetcher.data,
    guestsCount.male,
    guestsCount.female,
    setBookingCart,
  ]);

  useEffect(() => {
    if (currentStep > 7 && !token && !hasSignupCustomerId) {
      setCurrentStep(7);
      openAuthModal("login", "booking");
    }
  }, [currentStep, token, hasSignupCustomerId, openAuthModal]);

  useEffect(() => {
    if (currentStep === 7 && !token && !hasSignupCustomerId) {
      const timeoutId = setTimeout(() => {
        openAuthModal("login", "booking");
      }, 1200);
      return () => clearTimeout(timeoutId);
    }
  }, [currentStep, token, hasSignupCustomerId, openAuthModal]);

  useEffect(() => {
    if (authModalFrom === "booking") {
      wasBookingModalRef.current = true;
    }
  }, [authModalFrom]);

  useEffect(() => {
    if (!authModalOpened && wasBookingModalRef.current && !token) {
      wasBookingModalRef.current = false;
      hasProcessedSignupCartRef.current = false;
    }
  }, [authModalOpened, token]);

  useEffect(() => {
    if (
      hasSignupCustomerId &&
      pendingCartBeforeSignup &&
      !token &&
      !hasProcessedSignupCartRef.current
    ) {
      hasProcessedSignupCartRef.current = true;
      const cartToRestore = {
        BasketItems: pendingCartBeforeSignup.BasketItems || [],
        PersonalReferentData:
          pendingCartBeforeSignup.PersonalReferentData || [],
        DiscountCodes: pendingCartBeforeSignup.DiscountCodes || [],
      };

      const savedCustomReferentData =
        bookingCart?.customReferentData ||
        pendingCartBeforeSignup.customReferentData;
      const savedCustomGuestsData =
        bookingCart?.customGuestsData ||
        pendingCartBeforeSignup.customGuestsData;

      const submitAddToCart = async () => {
        await initSession();
        await performAddToCart(cartToRestore);
      };
      submitAddToCart();
    }
  }, [
    hasSignupCustomerId,
    pendingCartBeforeSignup,
    token,
    initSession,
    performAddToCart,
  ]);

  useEffect(() => {
    if (
      authModalOpened &&
      authModalFrom === "booking" &&
      bookingCart &&
      !token
    ) {
      setPendingCartBeforeSignup(bookingCart);
      savedCustomDataRef.current = {
        customReferentData: bookingCart.customReferentData,
        customGuestsData: bookingCart.customGuestsData,
      };
    }
  }, [authModalOpened, authModalFrom, bookingCart, token]);

  useEffect(() => {
    if (
      bookingCart &&
      (bookingCart.customReferentData || bookingCart.customGuestsData) &&
      !token
    ) {
      savedCustomDataRef.current = {
        customReferentData: bookingCart.customReferentData,
        customGuestsData: bookingCart.customGuestsData,
      };
    }
  }, [bookingCart?.customReferentData, bookingCart?.customGuestsData, token]);

  useEffect(() => {
    if (token && authModalFrom === "booking") {
      setHasSignupCustomerId(false);

      if (pendingCartBeforeSignup) {
        if (!hasProcessedSignupCartRef.current) {
          hasProcessedSignupCartRef.current = true;
          const cartToRestore = {
            BasketItems: pendingCartBeforeSignup.BasketItems || [],
            PersonalReferentData:
              pendingCartBeforeSignup.PersonalReferentData || [],
            DiscountCodes: pendingCartBeforeSignup.DiscountCodes || [],
          };
          const submitAddToCart = async () => {
            await initSession();
            await performAddToCart(cartToRestore, "/api/booking/add-to-cart");
          };
          submitAddToCart();
        }
        setPendingCartBeforeSignup(null);
      } else if (pendingUpsellingCart) {
        setLoadingAddToCart(true);
        setHasAddToCartErrors(false);
        addToCartUpsellingFetcher.submit(pendingUpsellingCart, {
          method: "post",
          action: `/api/booking/add-to-cart${
            isVoucherBooking ? "?isVoucherBooking=true" : ""
          }`,
          encType: "application/json",
        });
        setPendingUpsellingCart(null);
      } else if (currentStep < 7) {
        setCurrentStep(7);
      }
    }
  }, [
    token,
    authModalFrom,
    currentStep,
    pendingUpsellingCart,
    pendingCartBeforeSignup,
    initSession,
  ]);

  const normalizeTime = useCallback((time: string): string => {
    if (!time) return "";
    // Handle ISO format with T (e.g., "2024-01-01T12:00:00.000Z")
    if (time.includes("T")) {
      const timePart = time.split("T")[1];
      if (timePart) {
        // Remove milliseconds and timezone (Z or +00:00)
        return timePart.split(".")[0].split("Z")[0].split("+")[0];
      }
      return time;
    }
    // Handle format with Z at the end (e.g., "12:00:00.000Z")
    if (time.endsWith("Z")) {
      return time.split(".")[0].split("Z")[0];
    }
    // Handle format with milliseconds (e.g., "12:00:00.000")
    return time.split(".")[0];
  }, []);

  const timesMatch = useCallback(
    (time1: string, time2: string): boolean => {
      return normalizeTime(time1) === normalizeTime(time2);
    },
    [normalizeTime],
  );

  const getSelectedTimesFromCart = useCallback(
    (serviceCode: string, availabilityList: any[]) => {
      if (!bookingCart?.BasketItems || !availabilityList) {
        return [];
      }

      const cartItemsForService = bookingCart.BasketItems.filter(
        (item: any) =>
          item.Role === "INTEGRATION" && item.Service === serviceCode,
      );

      const selectedTimes: any[] = [];

      cartItemsForService.forEach((cartItem: any) => {
        const matchingAvailability = availabilityList.find(
          (avail: any) =>
            timesMatch(avail.StartTime, cartItem.StartTime) &&
            timesMatch(avail.EndTime, cartItem.EndTime),
        );

        if (
          matchingAvailability &&
          !selectedTimes.some(
            (t: any) =>
              timesMatch(t.StartTime, matchingAvailability.StartTime) &&
              timesMatch(t.EndTime, matchingAvailability.EndTime),
          )
        ) {
          selectedTimes.push(matchingAvailability);
        }
      });

      return selectedTimes;
    },
    [bookingCart, timesMatch],
  );

  const getSelectedTimeFromCart = useCallback(
    (serviceCode: string, availabilityList: any[]) => {
      if (!bookingCart?.BasketItems || !availabilityList) {
        return null;
      }

      const mainServiceItem = bookingCart.BasketItems.find(
        (item: any) => item.Role === "MAIN" && item.Service === serviceCode,
      );

      if (!mainServiceItem) {
        return null;
      }

      const matchingAvailability = availabilityList.find(
        (avail: any) =>
          timesMatch(avail.StartTime, mainServiceItem.StartTime) &&
          timesMatch(avail.EndTime, mainServiceItem.EndTime),
      );

      return matchingAvailability || null;
    },
    [bookingCart, timesMatch],
  );

  useEffect(() => {
    if (
      selectedUpsellingService &&
      selectedUpsellingService.Availability &&
      selectedUpsellingService.Availability.length > 0 &&
      bookingCart &&
      selectedUpsellingTime.length === 0 &&
      !userDeselectedAllUpsellingRef.current
    ) {
      const existingTimes = getSelectedTimesFromCart(
        selectedUpsellingService.Code,
        selectedUpsellingService.Availability,
      );
      if (existingTimes.length > 0) {
        setSelectedUpsellingTime(existingTimes);
        userDeselectedAllUpsellingRef.current = false;
      }
    } else if (selectedUpsellingTime.length > 0) {
      userDeselectedAllUpsellingRef.current = false;
    }
  }, [
    selectedUpsellingService,
    bookingCart,
    getSelectedTimesFromCart,
    selectedUpsellingTime.length,
  ]);

  const previousStepRef = useRef<number>(currentStep);

  useEffect(() => {
    if (currentStep < previousStepRef.current && currentStep < 6) {
      setSelectedUpsellingService(undefined);
      setSelectedUpsellingTime([]);
      setSelectedUpsellingQuantity(guestsCount.male + guestsCount.female);
    }
    previousStepRef.current = currentStep;
  }, [currentStep, guestsCount.male, guestsCount.female]);

  // La selezione pre-conferma dello step servizio vive solo mentre siamo sullo
  // step 3. Quando si lascia lo step (conferma/indietro/reset) la azzeriamo, così
  // a un nuovo ingresso non resta una preselezione stantia. Mentre si resta sullo
  // step 3 (anche in caso di rimonta) persiste e ripristina la selezione.
  useEffect(() => {
    if (currentStep !== 3) {
      setServiceTypeSelection(null);
    }
  }, [currentStep]);

  const hasAutoSkippedStep4Ref = useRef(false);

  /**
   * PACKAGE voucher: when the covered packages differ only in treatment
   * duration they must not be offered as a choice — we keep the longest one and
   * use the single-package path, which is also what `handleAddPackageToCart`
   * assumes. When they are genuinely different packages the customer's choice
   * stays.
   *
   * Returns `true` when it handled the selection, so callers keep their own
   * behaviour untouched in every other case.
   */
  const collapseVoucherDurationVariants = useCallback(
    (packages: any[]) => {
      if (VOUCHER_PACKAGE_ALTERNATIVES_SELECTABLE) return false;
      if (!packages || packages.length <= 1) return false;
      if (!areDurationVariants(packages, rootData?.servicesData || [])) {
        return false;
      }
      setSelectedPackage(pickLongestTreatmentPackage(packages));
      setSelectedPackages([]);
      return true;
    },
    [rootData?.servicesData],
  );

  useEffect(() => {
    if (
      currentStep === 4 &&
      services.length > 0 &&
      selectedCmsService &&
      !hasAutoSkippedStep4Ref.current
    ) {
      const vouchers = isVoucherBooking ? bookingCart?.vouchers || [] : [];
      let filteredServices = services;

      if (vouchers.length > 0) {
        const nonUpsellingVouchers = vouchers.filter(
          (v) => v.voucherData?.OnlyIntegration !== true,
        );

        if (nonUpsellingVouchers.length === 0) {
          filteredServices = services.filter((s) => s.Type !== "Package");
        } else {
          const voucherServicesIncluded = nonUpsellingVouchers.reduce(
            (acc: string[], v) => {
              const codes = v.voucherData?.ServicesIncludedCodes || [];
              return [...acc, ...codes];
            },
            [],
          );
          const uniqueCodes = Array.from(new Set(voucherServicesIncluded));

          filteredServices = servicesCoveredByAllVouchers(
            services.filter((s) => uniqueCodes.includes(s.Code)),
            nonUpsellingVouchers.map((v) => v.voucherData),
          );

          // Voucher: se è presente almeno un package, mostra solo package
          const packagesInFiltered = filteredServices.filter(
            (s) => s.Type === "Package",
          );
          if (packagesInFiltered.length > 0) {
            filteredServices = packagesInFiltered;
          }
        }
      }

      if (filteredServices.length === 1) {
        hasAutoSkippedStep4Ref.current = true;
        const singleService = filteredServices[0];
        setSkippedSteps((prev) => (prev.includes(4) ? prev : [...prev, 4]));
        if (singleService?.Type === "Package") {
          if (isVoucherBooking) {
            const vouchers = bookingCart?.vouchers || [];
            const allPackageCodes = vouchers.reduce((acc: string[], v) => {
              const codes = v.voucherData?.ServicesIncludedCodes || [];
              return [...acc, ...codes];
            }, []);
            const uniquePackageCodes = Array.from(new Set(allPackageCodes));
            const allPackages = services.filter(
              (s: any) =>
                s.Type === "Package" && uniquePackageCodes.includes(s.Code),
            );
            if (allPackages.length > 1) {
              if (!collapseVoucherDurationVariants(allPackages)) {
                setSelectedPackages(allPackages);
                setSelectedPackage(undefined);
              }
            } else {
              setSelectedPackage(singleService);
              setSelectedPackages([]);
            }
          } else {
            setSelectedPackage(singleService);
            setSelectedPackages([]);
          }
          setSelectedService(singleService);
          setSkippedSteps((prev) => {
            const newSkipped = [...prev];
            if (newSkipped.includes(4)) {
              const idx = newSkipped.indexOf(4);
              newSkipped.splice(idx, 1);
            }
            if (!newSkipped.includes(5)) newSkipped.push(5);
            return newSkipped;
          });
          setCurrentStep(4.5);
        } else {
          setSelectedService(singleService);
          setCurrentStep(5);
        }
      }
    }

    if (currentStep <= 3) {
      hasAutoSkippedStep4Ref.current = false;
    }
  }, [
    currentStep,
    services,
    selectedCmsService,
    bookingCart?.vouchers,
    collapseVoucherDurationVariants,
  ]);

  const getVoucherForService = useCallback(
    (serviceCode: string, vouchers: any[]) => {
      if (!vouchers || vouchers.length === 0) return null;

      const vouchersForService = vouchers.filter((v) => {
        if (
          !v.voucherData.ServicesIncludedCodes ||
          v.voucherData.ServicesIncludedCodes.length === 0
        ) {
          return true;
        }
        return v.voucherData.ServicesIncludedCodes.includes(serviceCode);
      });

      if (vouchersForService.length === 0) return null;

      return vouchersForService.reduce((prev, curr) => {
        const prevExp = prev.voucherData.ExpirationDate
          ? new Date(prev.voucherData.ExpirationDate)
          : null;
        const currExp = curr.voucherData.ExpirationDate
          ? new Date(curr.voucherData.ExpirationDate)
          : null;

        if (!prevExp) return curr;
        if (!currExp) return prev;

        return currExp < prevExp ? curr : prev;
      });
    },
    [],
  );

  const buildUpsellingCart = useCallback(
    (quantity?: number) => {
      if (!selectedDate || !selectedUpsellingService) {
        return null;
      }

      const vouchers = isVoucherBooking ? bookingCart?.vouchers || [] : [];
      let newBasketItems: any = bookingCart?.BasketItems || [];

      if (selectedUpsellingTime && selectedUpsellingTime.length > 0) {
        const selectedTimeKeys = new Set(
          selectedUpsellingTime.map(
            (time: any) =>
              `${normalizeTime(time.StartTime)}-${normalizeTime(time.EndTime)}`,
          ),
        );

        newBasketItems = newBasketItems.filter((item: any) => {
          if (item.Service !== selectedUpsellingService.Code) {
            return true;
          }
          if (item.Role !== "INTEGRATION") {
            return true;
          }
          const itemTimeKey = `${normalizeTime(item.StartTime)}-${normalizeTime(
            item.EndTime,
          )}`;
          return !selectedTimeKeys.has(itemTimeKey);
        });
      }

      if (!selectedUpsellingTime || selectedUpsellingTime.length === 0) {
        if (newBasketItems.length === 0) {
          return null;
        }
        let lineNr = 1;
        const renumberedItems = newBasketItems.map((item: any) => ({
          ...item,
          LineNr: lineNr++,
        }));
        return {
          BasketItems: renumberedItems,
          PersonalReferentData: [],
          ...(bookingCart?.DiscountCodes && {
            DiscountCodes: bookingCart.DiscountCodes,
          }),
        };
      }

      const qty = quantity ?? selectedUpsellingQuantity;
      const vouchersForService = isVoucherBooking
        ? vouchers.filter((v) => {
            if (
              !v.voucherData.ServicesIncludedCodes ||
              v.voucherData.ServicesIncludedCodes.length === 0
            ) {
              return true;
            }
            return v.voucherData.ServicesIncludedCodes.includes(
              selectedUpsellingService.Code,
            );
          })
        : [];

      const maxLineNr =
        newBasketItems.length > 0
          ? Math.max(...newBasketItems.map((item: any) => item.LineNr || 0))
          : 0;
      let lineNr = maxLineNr + 1;
      let voucherAppliedCount = 0;
      const maxVoucherApplications = vouchersForService.length;

      selectedUpsellingTime.forEach((time: any) => {
        for (let i = 0; i < qty; i++) {
          const basketItem: any = {
            Type: "SERVICE",
            ServiceType: selectedUpsellingService.Type.toUpperCase(),
            LineNr: lineNr++,
            Service: selectedUpsellingService.Code,
            Role: "INTEGRATION",
            CheckinDate: selectedDate.toISOString().split("T")[0],
            StartTime: time.StartTime,
            EndTime: time.EndTime,
            Quantity: {
              Total: 1,
            },
            GrossPrice: time.EffectivePrice?.[0] || {
              Price: time.Price || 0,
              Currency: "EUR",
            },
          };

          if (
            isVoucherBooking &&
            vouchersForService.length > 0 &&
            voucherAppliedCount < maxVoucherApplications
          ) {
            const voucherToApply = vouchersForService[voucherAppliedCount];
            basketItem.VoucherCode = voucherToApply.code;
            basketItem.GiftCardType = voucherToApply.giftCardType;
            basketItem.GrossAmount = {
              Price: 0,
              VatIncluded: true,
              Currency: "EUR",
            };
            basketItem.GrossPrice = {
              Price: 0,
              VatIncluded: true,
              Currency: "EUR",
            };
            voucherAppliedCount++;
          }

          newBasketItems.push(basketItem);
        }
      });

      return {
        BasketItems: newBasketItems,
        PersonalReferentData: [],
      };
    },
    [
      selectedDate,
      selectedUpsellingService,
      selectedUpsellingTime,
      selectedUpsellingQuantity,
      bookingCart,
      getVoucherForService,
      isVoucherBooking,
    ],
  );

  const handleAddToCartUpselling = async (quantity?: number) => {
    const newBookingCart = buildUpsellingCart(quantity);
    if (!newBookingCart) return;

    if (!token && !hasSignupCustomerId) {
      try {
        await initSession();
      } catch (error) {
        console.error("Failed to init session:", error);
      }
    }

    setLoadingAddToCart(true);
    setHasAddToCartErrors(false);
    setBookingCart(bookingCart);

    addToCartUpsellingFetcher.submit(newBookingCart, {
      method: "post",
      action: `/api/booking/add-to-cart${
        isVoucherBooking ? "?isVoucherBooking=true" : ""
      }`,
      encType: "application/json",
    });
  };

  const buildCartWithoutItem = useCallback(
    (itemToRemove: any) => {
      if (!bookingCart?.BasketItems) {
        return null;
      }

      const newBasketItems = bookingCart.BasketItems.filter(
        (item: any) => item.BasketItemId !== itemToRemove.BasketItemId,
      );

      if (newBasketItems.length === 0) {
        return null;
      }

      let lineNr = 1;
      const renumberedItems = newBasketItems.map((item: any) => ({
        ...item,
        LineNr: lineNr++,
      }));

      return {
        BasketItems: renumberedItems,
        PersonalReferentData: [],
        ...(bookingCart.DiscountCodes && {
          DiscountCodes: bookingCart.DiscountCodes,
        }),
      };
    },
    [bookingCart],
  );

  const handleRemoveFromCart = useCallback(
    async (item: any, isMainService: boolean) => {
      setLoadingAddToCart(true);
      setHasAddToCartErrors(false);

      if (isMainService) {
        setPreviousStep(0);
        stepKeyRef.current += 1;
        setBookingCart(null);
        setCurrentStep(0);
        setSelectedDate(undefined);
        setSelectedAvailability(undefined);
        setSelectedCmsService(undefined);
        setSelectedApiService(undefined);
        setSelectedService(undefined);
        setSelectedTime(undefined);
        setSelectedPackage(undefined);
        setSelectedPackages([]);
        setPackageData(undefined);
        setSelectedUpsellingService(undefined);
        setSelectedUpsellingTime([]);
        setSelectedUpsellingQuantity(guestsCount.male + guestsCount.female);
        setHasAddedToCartInThisFlow(false);

        if (!token && !hasSignupCustomerId) {
          try {
            await initSession();
          } catch (error) {
            console.error("Failed to init session:", error);
            setLoadingAddToCart(false);
            return;
          }
        }

        removeFromCartFetcher.submit(null, {
          method: "delete",
          action: `/api/booking/delete-basket${
            isVoucherBooking ? "?isVoucherBooking=true" : ""
          }`,
        });
      } else {
        const newBookingCart = buildCartWithoutItem(item);

        if (!newBookingCart) {
          setBookingCart(null);
          setCurrentStep(0);
          setHasAddedToCartInThisFlow(false);
          setLoadingAddToCart(false);
          return;
        }

        if (!token && !hasSignupCustomerId) {
          try {
            await initSession();
          } catch (error) {
            console.error("Failed to init session:", error);
            setLoadingAddToCart(false);
            return;
          }
        }

        removeFromCartFetcher.submit(newBookingCart, {
          method: "post",
          action: `/api/booking/add-to-cart${
            isVoucherBooking ? "?isVoucherBooking=true" : ""
          }`,
          encType: "application/json",
        });
      }
    },
    [
      buildCartWithoutItem,
      token,
      hasSignupCustomerId,
      removeFromCartFetcher,
      setBookingCart,
      setCurrentStep,
      setSelectedDate,
      setSelectedAvailability,
      setSelectedCmsService,
      setSelectedApiService,
      setSelectedService,
      setSelectedTime,
      setSelectedPackage,
      setPackageData,
      setSelectedUpsellingService,
      setSelectedUpsellingTime,
      setSelectedUpsellingQuantity,
      guestsCount.male,
      guestsCount.female,
    ],
  );

  const handleAddPackageToCart = (packageData: any) => {
    if (!selectedDate || !packageData) return;
    setLoadingAddToCart(true);
    setHasAddToCartErrors(false);

    const hasMultiplePackages = selectedPackages && selectedPackages.length > 1;

    const vouchers = isVoucherBooking ? bookingCart?.vouchers || [] : [];
    const vouchersForPackage = isVoucherBooking
      ? vouchers.filter((v) => {
          if (
            !v.voucherData.ServicesIncludedCodes ||
            v.voucherData.ServicesIncludedCodes.length === 0
          ) {
            return true;
          }
          return v.voucherData.ServicesIncludedCodes.includes(
            packageData.package.apiService?.Code || packageData.package.Code,
          );
        })
      : [];

    const newBasketItems: any[] = [];
    let lineNr = 1;
    const selectedPackage = packageData.package;
    const entranceTime = packageData.entranceTime;
    const selectedTimes = packageData.selectedTimes || [];

    const packageNumPax =
      selectedPackage.apiService?.NumPax || selectedPackage?.NumPax || 1;

    const packageComposition =
      selectedPackage.apiService?.PackageComposition ||
      selectedPackage.PackageComposition;
    const entranceStep = packageComposition?.find((s: any) => s.StepNr === 1);
    const treatmentStep = packageComposition?.find((s: any) => s.StepNr > 1);
    const treatmentNrRequested = treatmentStep?.NrRequested || 1;

    const packageVoucher = vouchersForPackage.find((v) => {
      if (
        !v.voucherData.ServicesIncludedCodes ||
        v.voucherData.ServicesIncludedCodes.length === 0
      ) {
        return true;
      }
      return v.voucherData.ServicesIncludedCodes.includes(
        selectedPackage.apiService?.Code || selectedPackage.Code,
      );
    });

    const totalGuests = guestsCount.male + guestsCount.female;
    const isVoucherFlow =
      isVoucherBooking && vouchersForPackage.length > 0 && !!packageVoucher;
    // One entry per package row: which guests it covers and, in the voucher
    // flow, which voucher pays for it.
    // - Normal flow: N packages = totalGuests / packageNumPax, priced from
    //   Booking_Search.
    // - Voucher flow: one package per voucher, each covering the voucher's
    //   NrPaxInvolved guests (two 1-person vouchers for 2 guests = two
    //   packages, each with its own entrance and treatment). Guests left
    //   over once the vouchers are used stay on the last voucher's package,
    //   which keeps a single voucher for the whole group on one row.
    const packageSlices: {
      startPax: number;
      totalPax: number;
      voucher?: any;
    }[] = [];
    if (isVoucherFlow) {
      // Upselling vouchers (OnlyIntegration) are spent on the upselling step
      // and do not count towards the guests either (GuestsSelect's
      // guestLimitingVouchers), so they never pay for a package row.
      const packageRowVouchers = vouchersForPackage.filter(
        (v) => v.voucherData?.OnlyIntegration !== true,
      );
      let startPax = 0;
      for (const voucher of packageRowVouchers.length > 0
        ? packageRowVouchers
        : [packageVoucher]) {
        if (startPax >= totalGuests) break;
        const voucherPax = Math.max(
          1,
          voucher.voucherData?.NrPaxInvolved || 1,
        );
        const totalPax = Math.min(voucherPax, totalGuests - startPax);
        packageSlices.push({ startPax, totalPax, voucher });
        startPax += totalPax;
      }
      if (startPax < totalGuests && packageSlices.length > 0) {
        packageSlices[packageSlices.length - 1].totalPax +=
          totalGuests - startPax;
      }
    } else {
      const numPackageItems = Math.max(
        1,
        Math.ceil(totalGuests / packageNumPax),
      );
      for (let pkgIndex = 0; pkgIndex < numPackageItems; pkgIndex++) {
        const startPax = pkgIndex * packageNumPax;
        packageSlices.push({
          startPax,
          totalPax: Math.max(
            0,
            Math.min(packageNumPax, totalGuests - startPax),
          ),
        });
      }
    }

    const packageAvailability =
      selectedPackage.apiService?.Availability?.find(
        (avail: any) => avail.Date === selectedDate.toISOString().split("T")[0],
      ) ||
      selectedPackage.apiService?.Availability?.[0] ||
      selectedPackage.Availability?.[0];

    for (const slice of packageSlices) {
      const pkgStartPax = slice.startPax;
      const thisPkgTotalPax = slice.totalPax;
      const thisPkgMale = Math.max(
        0,
        Math.min(guestsCount.male - pkgStartPax, thisPkgTotalPax),
      );

      const packageLineNr = lineNr++;

      const packageItem: any = {
        Type: "SERVICE",
        ServiceType:
          selectedPackage.apiService?.Type || selectedPackage.Type || "Package",
        Service: selectedPackage.apiService?.Code || selectedPackage.Code,
        LineNr: packageLineNr,
        Role: "MAIN",
        CheckinDate: selectedDate.toISOString().split("T")[0],
        StartTime: packageAvailability?.StartTime || "00:00:00",
        EndTime: packageAvailability?.EndTime || "23:59:59",
        Quantity: {
          Man: 0,
          Woman: 0,
          Total: 1,
        },
        GrossPrice: packageAvailability?.EffectivePrice?.[0] ||
          selectedPackage.apiService?.Availability?.[0]?.EffectivePrice?.[0] ||
          selectedPackage.Availability?.[0]?.EffectivePrice?.[0] || {
            Price: 0,
            Currency: "EUR",
            VatIncluded: true,
          },
        Included: false,
        RefLine: 0,
      };

      if (slice.voucher) {
        const voucherToApply = slice.voucher;
        packageItem.VoucherCode = voucherToApply.code;
        packageItem.GiftCardType = voucherToApply.giftCardType;
        packageItem.GrossAmount = {
          Price: 0,
          VatIncluded: true,
          Currency: "EUR",
        };
        packageItem.GrossPrice = {
          Price: 0,
          VatIncluded: true,
          Currency: "EUR",
        };
      }

      newBasketItems.push(packageItem);

      if (entranceTime && entranceStep?.ServicesList) {
        const entranceService = entranceStep.ServicesList.find((s: any) =>
          s.Availability?.some(
            (a: any) =>
              a.StartTime === entranceTime.StartTime &&
              a.EndTime === entranceTime.EndTime,
          ),
        );

        if (entranceService) {
          const entranceServiceNumPax = entranceService.NumPax || 1;
          const hasGenderDistinction = entranceService.GenderDistinction;

          const numBasketItems = Math.ceil(
            thisPkgTotalPax / entranceServiceNumPax,
          );

          for (let itemIndex = 0; itemIndex < numBasketItems; itemIndex++) {
            const startPaxIndex = itemIndex * entranceServiceNumPax;
            const endPaxIndex = Math.min(
              startPaxIndex + entranceServiceNumPax,
              thisPkgTotalPax,
            );
            const itemNumPax = endPaxIndex - startPaxIndex;

            const itemMaleCount = hasGenderDistinction
              ? Math.max(
                  0,
                  Math.min(thisPkgMale - startPaxIndex, itemNumPax),
                )
              : 0;
            const itemFemaleCount = hasGenderDistinction
              ? itemNumPax - itemMaleCount
              : 0;

            newBasketItems.push({
              Type: "SERVICE",
              ServiceType: entranceService.Type || "Ticket",
              LineNr: lineNr++,
              Service: entranceService.Code,
              Role: "INCLUDED",
              CheckinDate: selectedDate.toISOString().split("T")[0],
              StartTime: entranceTime.StartTime,
              EndTime: entranceTime.EndTime,
              Quantity: {
                Man: itemMaleCount,
                Woman: itemFemaleCount,
                Total: 1,
              },
              GrossPrice: {
                Price: 0,
                Currency: "EUR",
                VatIncluded: true,
              },
              Included: true,
              RefLine: packageLineNr,
            });
          }
        }
      }

      let selectedTimesIndex = 0;
      for (let i = 0; i < treatmentNrRequested; i++) {
        if (selectedTimesIndex < selectedTimes.length) {
          const treatmentTime = selectedTimes[selectedTimesIndex++];

          const treatmentService = packageComposition
            ?.find((step: any) => step.StepNr > 1)
            ?.ServicesList?.find(
              (s: any) => s.Code === treatmentTime.ServiceCode,
            );

          const treatmentTimeNumPax = treatmentService?.NumPax || 1;
          const hasGenderDistinction =
            treatmentService?.GenderDistinction || false;

          const startPaxIndex = i * treatmentTimeNumPax;
          const endPaxIndex = Math.min(
            startPaxIndex + treatmentTimeNumPax,
            thisPkgTotalPax,
          );
          const itemNumPax = endPaxIndex - startPaxIndex;

          if (itemNumPax <= 0) continue;

          const itemMaleCount = hasGenderDistinction
            ? Math.max(
                0,
                Math.min(thisPkgMale - startPaxIndex, itemNumPax),
              )
            : 0;
          const itemFemaleCount = hasGenderDistinction
            ? itemNumPax - itemMaleCount
            : 0;

          newBasketItems.push({
            Type: "SERVICE",
            ServiceType: treatmentTime.ServiceType || "Treatment",
            LineNr: lineNr++,
            Service: treatmentTime.ServiceCode,
            Role: "INCLUDED",
            CheckinDate: selectedDate.toISOString().split("T")[0],
            StartTime: treatmentTime.StartTime,
            EndTime: treatmentTime.EndTime,
            Quantity: {
              Total: 1,
              Man: itemMaleCount,
              Woman: itemFemaleCount,
            },
            GrossPrice: {
              Price: 0,
              Currency: "EUR",
              VatIncluded: true,
            },
            Included: true,
            RefLine: packageLineNr,
          });
        }
      }
    }

    const currentCart = bookingCart;
    const bookingCartToSubmit = {
      BasketItems: newBasketItems,
      PersonalReferentData: [],
      ...(currentCart?.DiscountCodes && {
        DiscountCodes: currentCart.DiscountCodes,
      }),
    };
    setBookingCart(bookingCartToSubmit);

    performAddToCart(bookingCartToSubmit);
  };

  const handleAddToCart = (service: any) => {
    if (!selectedDate) return;
    if (!service) {
      setHasAddToCartErrors({
        error: t("Qualcosa è andato storto! Ricarica la pagina e riprova"),
      });
      return;
    }
    setLoadingAddToCart(true);
    setHasAddToCartErrors(false);

    const vouchers = isVoucherBooking ? bookingCart?.vouchers || [] : [];
    const vouchersForService = isVoucherBooking
      ? vouchers.filter((v) => {
          if (
            !v.voucherData.ServicesIncludedCodes ||
            v.voucherData.ServicesIncludedCodes.length === 0
          ) {
            return true;
          }
          return v.voucherData.ServicesIncludedCodes.includes(
            selectedService.Code,
          );
        })
      : [];

    const newBasketItems = [];
    let lineNr = 1;

    const cmsCategory = getCmsServiceCategoryFromList(
      rootData?.serviceCategoriesData || [],
      selectedAvailability.category,
    );
    const needGenderDistinction = !!cmsCategory?.acf?.need_gender_distinction;
    let voucherAppliedCount = 0;
    const maxVoucherApplications = vouchersForService.length;

    for (let i = 0; i < guestsCount.male; i++) {
      const basketItem: any = {
        Type: "SERVICE",
        ServiceType: selectedService.Type.toUpperCase(),
        LineNr: lineNr++,
        Service: selectedService.Code,
        Role: "MAIN",
        CheckinDate: selectedDate.toISOString().split("T")[0],
        StartTime: service.StartTime,
        EndTime: service.EndTime,
        Quantity: {
          Man: needGenderDistinction ? 1 : 0,
          Woman: 0,
          Total: 1,
        },
        GrossPrice: service.EffectivePrice[0],
      };

      if (
        isVoucherBooking &&
        vouchersForService.length > 0 &&
        voucherAppliedCount < maxVoucherApplications
      ) {
        const voucherToApply = vouchersForService[voucherAppliedCount];
        basketItem.VoucherCode = voucherToApply.code;
        basketItem.GiftCardType = voucherToApply.giftCardType;
        basketItem.GrossAmount = {
          Price: 0,
          VatIncluded: true,
          Currency: "EUR",
        };
        basketItem.GrossPrice = {
          Price: 0,
          VatIncluded: true,
          Currency: "EUR",
        };
        voucherAppliedCount++;
      }

      newBasketItems.push(basketItem);
    }

    for (let i = 0; i < guestsCount.female; i++) {
      const basketItem: any = {
        Type: "SERVICE",
        ServiceType: "TICKET",
        LineNr: lineNr++,
        Service: selectedService.Code,
        Role: "MAIN",
        CheckinDate: selectedDate.toISOString().split("T")[0],
        StartTime: service.StartTime,
        EndTime: service.EndTime,
        Quantity: {
          Man: 0,
          Woman: needGenderDistinction ? 1 : 0,
          Total: 1,
        },
        GrossPrice: service.EffectivePrice[0],
      };

      if (
        isVoucherBooking &&
        vouchersForService.length > 0 &&
        voucherAppliedCount < maxVoucherApplications
      ) {
        const voucherToApply = vouchersForService[voucherAppliedCount];
        basketItem.VoucherCode = voucherToApply.code;
        basketItem.GiftCardType = voucherToApply.giftCardType;
        basketItem.GrossAmount = {
          Price: 0,
          VatIncluded: true,
          Currency: "EUR",
        };
        basketItem.GrossPrice = {
          Price: 0,
          VatIncluded: true,
          Currency: "EUR",
        };
        voucherAppliedCount++;
      }

      newBasketItems.push(basketItem);
    }

    const currentCart = bookingCart;
    const bookingCartToSubmit = {
      BasketItems: newBasketItems,
      PersonalReferentData: [],
      ...(currentCart?.DiscountCodes && {
        DiscountCodes: currentCart.DiscountCodes,
      }),
    };
    setBookingCart(bookingCartToSubmit);

    performAddToCart(bookingCartToSubmit);
  };

  const stepAnimationVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? "100px" : "-100px",
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? "100px" : "-100px",
      opacity: 0,
    }),
  };

  return (
    <div
      className={`
        relative
      `}
    >
      <main className="pb-12 pt-12 lg:pt-12 overflow-x-hidden">
        {!isVoucherBookingWithoutVouchers && (
          <div className="container mx-auto px-4 lg:pr-96 relative">
            <h3 className="w-full block text-center text-xl font-bold lg:max-w-none max-w-64 mx-auto">
              {t("Prenota la tua esperienza ChorusLife Immersive SPA")}
            </h3>
            <div className="w-full max-w-5xl mx-auto flex flex-col items-center justify-center mt-4 relative">
              <div className="flex flex-row flex-nowrap items-center justify-center lg:pb-0 relative">
                <button
                  onClick={handleBack}
                  disabled={
                    currentStep === 0 || isVoucherBookingWithoutVouchers
                  }
                  className={cn(
                    "absolute -left-12 -top-1 p-2 rounded-full transition-all duration-200",
                    {
                      "text-gray-400 cursor-not-allowed":
                        currentStep === 0 || isVoucherBookingWithoutVouchers,
                      "text-secondary cursor-pointer":
                        currentStep > 0 && !isVoucherBookingWithoutVouchers,
                    },
                  )}
                >
                  <ChevronRight className="w-6 h-6 rotate-180" size={24} />
                </button>

                <div className="flex flex-row flex-nowrap items-center">
                  {Array.from(Array(NUMBER_OF_STEPS).keys()).map(
                    (stepNumberTmp: number) => {
                      if (skippedSteps.indexOf(stepNumberTmp) !== -1) {
                        return null;
                      }
                      const stepNumber = stepNumberTmp;
                      let stepForCalculation = currentStep;
                      if (currentStep === 4.5) {
                        stepForCalculation = 4;
                      }
                      const nextVisibleStep = (() => {
                        let next = stepForCalculation + 1;
                        while (
                          next < NUMBER_OF_STEPS &&
                          skippedSteps.includes(next)
                        ) {
                          next++;
                        }
                        return next < NUMBER_OF_STEPS ? next : null;
                      })();
                      if (
                        nextVisibleStep !== null &&
                        stepNumber > nextVisibleStep
                      ) {
                        return null;
                      }
                      const visualStepNumber =
                        stepNumber -
                        skippedSteps.filter((s) => s < stepNumber).length;
                      let currentVisualStep =
                        stepForCalculation -
                        skippedSteps.filter((s) => s < stepForCalculation)
                          .length;
                      const prevVisibleStep = (() => {
                        let prev = stepForCalculation - 1;
                        while (prev >= 0 && skippedSteps.includes(prev)) {
                          prev--;
                        }
                        return prev;
                      })();

                      let isVisibleOnMobile = false;

                      if (currentStep === 0) {
                        isVisibleOnMobile = stepNumber <= 2;
                      } else if (currentStep === NUMBER_OF_STEPS - 1) {
                        isVisibleOnMobile = stepNumber >= NUMBER_OF_STEPS - 3;
                      } else {
                        if (nextVisibleStep === null) {
                          isVisibleOnMobile = stepNumber >= prevVisibleStep;
                        } else {
                          isVisibleOnMobile =
                            stepNumber >= prevVisibleStep &&
                            stepNumber <= nextVisibleStep;
                        }
                      }

                      let isLastVisibleOnMobile = false;
                      if (isVisibleOnMobile) {
                        if (currentStep === 0) {
                          isLastVisibleOnMobile = stepNumber === 2;
                        } else if (currentStep === NUMBER_OF_STEPS - 1) {
                          isLastVisibleOnMobile =
                            stepNumber === NUMBER_OF_STEPS - 1;
                        } else {
                          isLastVisibleOnMobile =
                            stepNumber === nextVisibleStep;
                        }
                      }

                      return (
                        <div
                          key={stepNumber}
                          data-key={stepNumber}
                          className={cn(
                            "last:mr-0 lg:-mr-2 flex flex-row flex-nowrap items-center group",
                            isVisibleOnMobile ? "flex" : "hidden",
                            "md:flex",
                            {
                              "-mr-2":
                                NUMBER_OF_STEPS !== stepNumber + 1 &&
                                !isLastVisibleOnMobile,
                            },
                          )}
                        >
                          <span
                            onClick={() => {
                              if (isVoucherBookingWithoutVouchers) return;
                              if (
                                currentStep > stepNumber &&
                                skippedSteps.indexOf(stepNumber) === -1
                              ) {
                                // Il cart viene popolato all'add del pacchetto
                                // (step 4.5). Tornare a stepNumber ≤ 4
                                // (ServiceDuration/Type/Day/Ospiti) con cart
                                // popolato rende lo stato incoerente: il
                                // ServiceTypeStep / ServiceDurationStep
                                // mostrerebbe varianti del pacchetto vecchio
                                // ("selezione degli ingressi che non centra
                                // nulla"). Quindi chiediamo conferma e poi
                                // svuotiamo il cart. Per gli step ≥ 5
                                // (upselling, checkout, payment) la
                                // navigazione è diretta perché il cart resta
                                // valido.
                                const hasItemsInCart =
                                  (bookingCart?.BasketItems?.length || 0) > 0;
                                const isPreCartTarget = stepNumber < 5;
                                if (hasItemsInCart && isPreCartTarget) {
                                  setPendingStepChange(stepNumber);
                                  setShowUpsellingBackConfirm(true);
                                } else {
                                  setSkippedSteps((prev) =>
                                    prev.filter((s) => s < stepNumber),
                                  );
                                  setCurrentStep(stepNumber);
                                }
                              }
                            }}
                            className={cn(
                              "font-bold w-8 min-w-8 h-8 rounded-full relative z-[1] flex items-center justify-center",
                              {
                                "cursor-pointer":
                                  skippedSteps.indexOf(stepNumber) === -1 &&
                                  currentVisualStep >= visualStepNumber &&
                                  currentVisualStep !== visualStepNumber,
                                "bg-secondary text-secondary-foreground":
                                  currentVisualStep >= visualStepNumber,
                                "bg-muted-800 text-white":
                                  currentVisualStep < visualStepNumber,
                              },
                            )}
                          >
                            {visualStepNumber + 1}
                          </span>
                          {NUMBER_OF_STEPS !== stepNumber + 1 && (
                            <div
                              className={cn(
                                "h-2 w-20 bg-gray-100 -ml-2 relative z-0 group-last:hidden",
                                isLastVisibleOnMobile
                                  ? "hidden md:block"
                                  : "block",
                              )}
                            >
                              <motion.div
                                key={`step-divider-${stepNumber}-${
                                  currentStep > stepNumber
                                    ? "completed"
                                    : "pending"
                                }`}
                                initial={{ width: 0 }}
                                animate={
                                  currentStep > stepNumber
                                    ? { width: "100%" }
                                    : { width: 0 }
                                }
                                transition={{
                                  duration: 0.3,
                                  ease: "easeInOut",
                                }}
                                className={cn(
                                  "h-2 bg-secondary opacity-20 relative z-0",
                                )}
                              />
                            </div>
                          )}
                        </div>
                      );
                    },
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="container mx-auto">
          <div className="relative flex flex-row items-start justify-center overflow-visible">
            <div className="relative flex flex-col items-center justify-center overflow-visible flex-1">
              {bookingCartLoading && (
                <div className="w-full text-center py-20">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="h-12 w-12 animate-spin rounded-full border-4 border-solid border-secondary border-t-transparent"></div>
                    <span className="text-lg">
                      {t("Caricamento dati prenotazione...")}
                    </span>
                  </div>
                </div>
              )}
              {!bookingCartLoading && isVoucherBookingWithoutVouchers && (
                <div className="w-full text-center py-20">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 max-w-md">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-amber-800 text-lg text-left leading-tight">
                            {t(
                              "Questa pagina è accessibile solo per prenotazioni con voucher",
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {!bookingCartLoading && !isVoucherBookingWithoutVouchers && (
                <AnimatePresence initial={false} custom={direction} mode="wait">
                  {currentStep === 0 && (
                    <motion.div
                      key={`step-0-${stepKeyRef.current}`}
                      custom={direction}
                      variants={stepAnimationVariants}
                      onUpdate={keepAnimationOnMainThread}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                      }}
                      className="w-full"
                    >
                      <GuestsSelect
                        disableConfirm={
                          currentStep > 0 || isVoucherBookingWithoutVouchers
                        }
                        defaultMaleCount={
                          bookingCart?.customGuestsData?.filter(
                            (guest: any) => !guest.isFemale,
                          ).length ||
                          guestsCount.male ||
                          0
                        }
                        defaultFemaleCount={
                          bookingCart?.customGuestsData?.filter(
                            (guest: any) => guest.isFemale,
                          ).length ||
                          guestsCount.female ||
                          0
                        }
                        guestLimitingVouchers={
                          isVoucherBooking
                            ? bookingCart?.vouchers?.filter(
                                // Tutti i voucher che vincolano gli ospiti:
                                // ingressi e pacchetti (esclusi gli upselling).
                                // Il totale ospiti = somma dei loro NrPaxInvolved.
                                (v) => v.voucherData?.OnlyIntegration !== true,
                              ) || []
                            : []
                        }
                        backUrl={backUrl}
                        onCancel={() => {}}
                        onChange={() => {
                          setSelectedDate(undefined);
                        }}
                        onConfirm={(maleCount, femaleCount) => {
                          setGuestsCount({
                            male: maleCount,
                            female: femaleCount,
                          });
                          setBookingCart({
                            ...(bookingCart || {}),
                            customGuestsData: [
                              ...Array.from({ length: maleCount }, () => ({
                                name: "",
                                isFemale: false,
                                surname: "",
                                email: "",
                              })),
                              ...Array.from({ length: femaleCount }, () => ({
                                name: "",
                                isFemale: true,
                                surname: "",
                                email: "",
                              })),
                            ],
                          });
                          setCurrentStep(1);
                        }}
                      />
                    </motion.div>
                  )}
                  {currentStep === 1 && (
                    <motion.div
                      key={1}
                      custom={direction}
                      variants={stepAnimationVariants}
                      onUpdate={keepAnimationOnMainThread}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                      }}
                      className="w-full"
                    >
                      <DaySelect
                        onCancel={() => {
                          setCurrentStep(0);
                          setSelectedDate(undefined);
                        }}
                        onChange={() => {
                          setCurrentStep(1);
                          setSelectedDate(undefined);
                        }}
                        disableConfirm={
                          currentStep > 1 || isVoucherBookingWithoutVouchers
                        }
                        guestsCount={guestsCount}
                        defaultDate={selectedDate}
                        isVoucherBooking={isVoucherBooking}
                        onConfirm={(
                          selectedDate: Date,
                          availabilites: any[],
                        ) => {
                          if (availabilites) {
                            setAvailabilitiesForDate(availabilites);
                            setSelectedDate(selectedDate);
                            setSelectedAvailability(undefined);
                            setCurrentStep(2);
                            setSelectedApiService(undefined);
                            setServices([]);
                          }
                        }}
                      />
                    </motion.div>
                  )}
                  {currentStep === 2 &&
                    selectedDate &&
                    availabilitiesForDate && (
                      <motion.div
                        key={2}
                        custom={direction}
                        variants={stepAnimationVariants}
                        onUpdate={keepAnimationOnMainThread}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{
                          type: "spring",
                          stiffness: 300,
                          damping: 30,
                        }}
                        className="w-full"
                      >
                        <ServiceCategoryStep
                          onSkip={(availability: any) => {
                            setSelectedAvailability(availability);
                            setSkippedSteps([2]);
                            setCurrentStep(3);
                          }}
                          availabilitiesForDate={availabilitiesForDate}
                          disableConfirm={
                            currentStep > 2 || isVoucherBookingWithoutVouchers
                          }
                          guestsCount={guestsCount}
                          selectedDate={selectedDate}
                          defaultSelectedAvailability={selectedAvailability}
                          onCancel={() => {
                            setCurrentStep(1);
                            setSelectedDate(undefined);
                          }}
                          onChange={() => {
                            setCurrentStep(2);
                            setSelectedAvailability(undefined);
                          }}
                          onConfirm={(selectedAvailability: any) => {
                            setCurrentStep(3);
                            setSelectedAvailability(selectedAvailability);
                          }}
                        />
                      </motion.div>
                    )}
                  {currentStep === 3 &&
                    selectedDate &&
                    selectedAvailability && (
                      <motion.div
                        key={3}
                        custom={direction}
                        variants={stepAnimationVariants}
                        onUpdate={keepAnimationOnMainThread}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{
                          type: "spring",
                          stiffness: 300,
                          damping: 30,
                        }}
                        className="w-full"
                      >
                        <ServiceTypeStep
                          setServices={setServices}
                          services={services}
                          disableConfirm={
                            currentStep > 3 || isVoucherBookingWithoutVouchers
                          }
                          guestsCount={guestsCount}
                          selectedDate={selectedDate}
                          selectedAvailability={selectedAvailability}
                          isVoucherBooking={isVoucherBooking}
                          defaultSelection={serviceTypeSelection}
                          onSelectionChange={(cmsService, apiService) =>
                            setServiceTypeSelection(
                              apiService ? { cmsService, apiService } : null,
                            )
                          }
                          onCancel={() => {
                            const targetStep = getPreviousVisibleStep(3);
                            setSkippedSteps((prev) =>
                              prev.filter((s) => s < targetStep),
                            );
                            setCurrentStep(targetStep);
                            setSelectedCmsService(undefined);
                            if (targetStep <= 1) {
                              setSelectedAvailability(undefined);
                            }
                          }}
                          onResetToStep0={() => {
                            setCurrentStep(0);
                            setSelectedCmsService(undefined);
                            setSelectedApiService(undefined);
                            setSelectedAvailability(undefined);
                            setSelectedDate(undefined);
                          }}
                          onChange={() => {
                            setCurrentStep(3);
                            setSelectedCmsService(undefined);
                          }}
                          onConfirm={(
                            selectedCmsService: any,
                            apiService?: any,
                            packages?: any[],
                          ) => {
                            // Salvo sempre selectedCmsService, anche nei
                            // rami pacchetto: serve come guard al rendering
                            // di `currentStep === 4` (ServiceDurationStep);
                            // se l'utente, dallo step 4.5, naviga via
                            // stepper allo step 4, senza questo valore il
                            // blocco non renderizza nulla (pagina bianca).
                            setSelectedCmsService(selectedCmsService);
                            if (packages && packages.length > 0) {
                              // Collapsing only applies to the voucher flow: in
                              // normal mode multiple packages stay a legitimate
                              // user choice.
                              if (
                                !isVoucherBooking ||
                                !collapseVoucherDurationVariants(packages)
                              ) {
                                setSelectedPackages(packages);
                                setSelectedPackage(undefined);
                              }
                              setSelectedService(undefined);
                              setSkippedSteps((prev) =>
                                prev.includes(3) ? prev : [...prev, 3],
                              );
                              setCurrentStep(4.5);
                            } else if (apiService?.Type === "Package") {
                              if (isVoucherBooking) {
                                const vouchers = bookingCart?.vouchers || [];
                                const allPackageCodes = vouchers.reduce(
                                  (acc: string[], v) => {
                                    const codes =
                                      v.voucherData?.ServicesIncludedCodes ||
                                      [];
                                    return [...acc, ...codes];
                                  },
                                  [],
                                );
                                const uniquePackageCodes = Array.from(
                                  new Set(allPackageCodes),
                                );
                                const allPackages = services.filter(
                                  (s: any) =>
                                    s.Type === "Package" &&
                                    uniquePackageCodes.includes(s.Code),
                                );
                                if (allPackages.length > 1) {
                                  if (
                                    !collapseVoucherDurationVariants(
                                      allPackages,
                                    )
                                  ) {
                                    setSelectedPackages(allPackages);
                                    setSelectedPackage(undefined);
                                  }
                                } else {
                                  setSelectedPackage(apiService);
                                  setSelectedPackages([]);
                                }
                              } else {
                                setSelectedPackage(apiService);
                                setSelectedPackages([]);
                              }
                              setSelectedService(apiService);
                              setSkippedSteps((prev) =>
                                prev.includes(3) ? prev : [...prev, 3],
                              );
                              setCurrentStep(4.5);
                            } else {
                              setSelectedCmsService(selectedCmsService);
                              setSelectedApiService(apiService);
                              setSkippedSteps((prev) =>
                                prev.includes(3) ? prev : [...prev, 3],
                              );
                              setCurrentStep(4);
                            }
                          }}
                        />
                      </motion.div>
                    )}
                  {currentStep === 4.5 &&
                    selectedDate &&
                    (selectedPackage ||
                      (selectedPackages && selectedPackages.length > 0)) && (
                      <motion.div
                        key={4.5}
                        custom={direction}
                        variants={stepAnimationVariants}
                        onUpdate={keepAnimationOnMainThread}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{
                          type: "spring",
                          stiffness: 300,
                          damping: 30,
                        }}
                        className="w-full"
                      >
                        <PackageStep
                          disableConfirm={
                            currentStep > 4.5 || isVoucherBookingWithoutVouchers
                          }
                          guestsCount={guestsCount}
                          selectedDate={selectedDate}
                          selectedPackage={selectedPackage}
                          selectedPackages={
                            selectedPackages.length > 0
                              ? selectedPackages
                              : undefined
                          }
                          loading={loadingAddToCart}
                          isVoucherBooking={isVoucherBooking}
                          onCancel={() => {
                            const targetStep = getPreviousVisibleStep(4.5);
                            if (targetStep <= 3) {
                              hasAutoSkippedStep4Ref.current = false;
                              setSelectedAvailability(undefined);
                              setSelectedDate(undefined);
                              setSelectedCmsService(undefined);
                              setSelectedService(undefined);
                            }
                            setSkippedSteps((prev) =>
                              prev.filter((s) => s < targetStep),
                            );
                            setCurrentStep(targetStep);
                            setSelectedPackage(undefined);
                            setSelectedPackages([]);
                          }}
                          onConfirm={(packageData: any) => {
                            setPackageData(packageData);
                            setLoadingAddToCart(true);
                            setHasAddToCartErrors(false);
                            handleAddPackageToCart(packageData);
                          }}
                        />
                      </motion.div>
                    )}
                  {currentStep === 4 &&
                    selectedDate &&
                    selectedAvailability &&
                    selectedCmsService && (
                      <motion.div
                        key={4}
                        custom={direction}
                        variants={stepAnimationVariants}
                        onUpdate={keepAnimationOnMainThread}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{
                          type: "spring",
                          stiffness: 300,
                          damping: 30,
                        }}
                        className="w-full"
                      >
                        <ServiceDurationStep
                          services={services}
                          disableConfirm={
                            currentStep > 4 || isVoucherBookingWithoutVouchers
                          }
                          selectedDate={selectedDate}
                          selectedAvailability={selectedAvailability}
                          selectedCmsService={selectedCmsService}
                          isVoucherBooking={isVoucherBooking}
                          totalGuests={guestsCount.male + guestsCount.female}
                          defaultSelectedService={
                            selectedService || selectedPackage
                          }
                          onCancel={() => {
                            const targetStep = getPreviousVisibleStep(4);
                            if (targetStep <= 3) {
                              hasAutoSkippedStep4Ref.current = false;
                            }
                            setSkippedSteps((prev) =>
                              prev.filter((s) => s < targetStep),
                            );
                            setCurrentStep(targetStep);
                            setSelectedService(undefined);
                            setSelectedPackage(undefined);
                            setSelectedPackages([]);
                          }}
                          onChange={() => {
                            let targetStep = 4;
                            while (
                              targetStep >= 0 &&
                              skippedSteps.includes(targetStep)
                            ) {
                              targetStep--;
                            }
                            setSkippedSteps((prev) =>
                              prev.filter((s) => s < targetStep),
                            );
                            setCurrentStep(targetStep);
                            setSelectedService(undefined);
                            setSelectedPackage(undefined);
                            setSelectedPackages([]);
                          }}
                          onConfirm={(selectedService: any) => {
                            if (selectedService?.Type === "Package") {
                              setSelectedPackage(selectedService);
                              setSelectedService(selectedService);
                              setSkippedSteps((prev) => {
                                const newSkipped = [...prev];
                                // Do not skip 4 because 4.5 takes its place visually
                                if (newSkipped.includes(4)) {
                                  const idx = newSkipped.indexOf(4);
                                  newSkipped.splice(idx, 1);
                                }
                                if (!newSkipped.includes(5)) newSkipped.push(5);
                                return newSkipped;
                              });
                              setCurrentStep(4.5);
                            } else {
                              setCurrentStep(5);
                              setSelectedService(selectedService);
                            }
                          }}
                        />
                      </motion.div>
                    )}
                  {currentStep === 5 && selectedDate && (
                    <motion.div
                      key={5}
                      custom={direction}
                      variants={stepAnimationVariants}
                      onUpdate={keepAnimationOnMainThread}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                      }}
                      className="w-full"
                    >
                      <TimeSelect
                        loading={loadingAddToCart}
                        selectedService={selectedService}
                        guestsCount={guestsCount}
                        selectedAvailability={selectedAvailability}
                        initialSelectedTime={
                          selectedService?.Code
                            ? getSelectedTimeFromCart(
                                selectedService.Code,
                                selectedService.Availability || [],
                              )
                            : null
                        }
                        onCancel={() => {
                          const targetStep = getPreviousVisibleStep(5);
                          setSkippedSteps((prev) =>
                            prev.filter((s) => s < targetStep),
                          );
                          setCurrentStep(targetStep);
                        }}
                        onConfirm={(selectedTimeFromAction: any) => {
                          setSelectedTime(selectedTimeFromAction);
                          handleAddToCart(selectedTimeFromAction);
                        }}
                      />
                    </motion.div>
                  )}
                  {currentStep === 6 && (
                    <motion.div
                      key={6}
                      custom={direction}
                      variants={stepAnimationVariants}
                      onUpdate={keepAnimationOnMainThread}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                      }}
                      className="w-full"
                    >
                      <UpsellingStep
                        externalLoading={loadingAddToCart}
                        selectedUpsellingService={selectedUpsellingService}
                        selectedUpsellingTime={selectedUpsellingTime}
                        selectedDate={selectedDate!}
                        guestsCount={guestsCount}
                        bookingCart={bookingCart}
                        isVoucherBooking={isVoucherBooking}
                        onLoadingChange={setUpsellingLoading}
                        onSelectService={(cmsService, apiService) => {
                          if (
                            selectedUpsellingService?.Code === apiService?.Code
                          ) {
                            setSelectedUpsellingService(undefined);
                            setSelectedUpsellingTime([]);
                            setSelectedUpsellingQuantity(
                              guestsCount.male + guestsCount.female,
                            );
                            return;
                          }
                          setSelectedUpsellingService(apiService);
                          const existingTimes = getSelectedTimesFromCart(
                            apiService.Code,
                            apiService.Availability || [],
                          );
                          setSelectedUpsellingTime(existingTimes);
                          setSelectedUpsellingQuantity(
                            guestsCount.male + guestsCount.female,
                          );
                        }}
                        onSelectTime={(time) => {
                          setSelectedUpsellingTime((prev: any[]) => {
                            const isAlreadySelected = prev.some(
                              (t: any) =>
                                timesMatch(t.StartTime, time.StartTime) &&
                                timesMatch(t.EndTime, time.EndTime),
                            );
                            if (isAlreadySelected) {
                              const newArray = prev.filter(
                                (t: any) =>
                                  !(
                                    timesMatch(t.StartTime, time.StartTime) &&
                                    timesMatch(t.EndTime, time.EndTime)
                                  ),
                              );
                              if (newArray.length === 0) {
                                userDeselectedAllUpsellingRef.current = true;
                              }
                              return newArray;
                            } else {
                              return [...prev, time];
                            }
                          });
                        }}
                        onSetSelectedTimes={(times) => {
                          setSelectedUpsellingTime(times);
                        }}
                        onCancel={() => {
                          const targetStep = getPreviousVisibleStep(6);
                          // Non tocchiamo gli skippedSteps qui: confermando
                          // la modale `confirmUpsellingBackNavigation`
                          // farà sia il filtro che il clearCart.
                          setPendingStepChange(targetStep);
                          setShowUpsellingBackConfirm(true);
                        }}
                        onBack={() => {
                          if (selectedUpsellingService) {
                            setSelectedUpsellingService(undefined);
                            setSelectedUpsellingTime([]);
                            setSelectedUpsellingQuantity(
                              guestsCount.male + guestsCount.female,
                            );
                          }
                        }}
                        onContinue={(quantity) => {
                          setSelectedUpsellingQuantity(quantity);
                          handleAddToCartUpselling(quantity);
                        }}
                        onSkip={() => {
                          goToStep7();
                        }}
                        onOpenCart={() => {
                          setMobileCartOpen(true);
                          setMobileCartOpenLocal(true);
                        }}
                      />
                    </motion.div>
                  )}
                  {currentStep === 7 && (
                    <motion.div
                      key={7}
                      custom={direction}
                      variants={stepAnimationVariants}
                      onUpdate={keepAnimationOnMainThread}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                      }}
                      className="w-full"
                    >
                      <CheckoutStep
                        actionData={actionData}
                        hasBookingCustomerToken={hasSignupCustomerId}
                        isDisabled={!token && !hasSignupCustomerId}
                        isVoucherBooking={isVoucherBooking}
                        guestsCount={guestsCount}
                        onCancel={() => {
                          setCurrentStep(6);
                        }}
                        onConfirm={() => {
                          setCurrentStep(8);
                        }}
                        disableConfirm={
                          currentStep > 7 || isVoucherBookingWithoutVouchers
                        }
                        onRemoveItem={handleRemoveFromCart}
                        loadingRemove={removeFromCartFetcher.state !== "idle"}
                      />
                    </motion.div>
                  )}
                  {currentStep === 8 && (
                    <motion.div
                      key={8}
                      custom={direction}
                      variants={stepAnimationVariants}
                      onUpdate={keepAnimationOnMainThread}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                      }}
                      className="w-full"
                    >
                      <PaymentStep
                        onCancel={() => {
                          goToStep7();
                        }}
                        onConfirm={() => {}}
                        disableConfirm={
                          currentStep > 8 || isVoucherBookingWithoutVouchers
                        }
                        isSubmitting={false}
                        isVoucherBooking={isVoucherBooking}
                        onLeavingForPaymentProvider={
                          handleLeavingForPaymentProvider
                        }
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>

            {!isVoucherBookingWithoutVouchers && (
              <div className="hidden lg:block right-0 top-16 bottom-0 overflow-y-auto">
                <BookingCartSidebar
                  onRemoveItem={handleRemoveFromCart}
                  loadingRemove={removeFromCartFetcher.state !== "idle"}
                  loadingAdd={
                    loadingAddToCart ||
                    addToCartUpsellingFetcher.state === "submitting"
                  }
                  hasAddedToCartInThisFlow={hasAddedToCartInThisFlow}
                  hideRemoveButtons={currentStep === 8}
                  isVoucherBooking={isVoucherBooking}
                  selectedDate={selectedDate}
                  guestsCount={guestsCount}
                  upsellingLoading={upsellingLoading}
                />
              </div>
            )}
          </div>

          {hasAddToCartErrors && (
            <div className="w-full bg-red-400 text-white p-4">
              <span className="text-white text-center block">
                {t("Qualcosa è andato storto! Ricarica la pagina e riprova")}
              </span>
            </div>
          )}
        </div>
      </main>

      <Dialog
        open={showSessionExpiredModal}
        onOpenChange={setShowSessionExpiredModal}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("Sessione scaduta")}</DialogTitle>
            <DialogDescription>
              {t(
                "La sessione del carrello è scaduta. Devi ricominciare dall'inizio per creare una nuova sessione.",
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => {
                setShowSessionExpiredModal(false);
                resetBookingToStep0();
              }}
            >
              {t("Ricomincia")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showUpsellingBackConfirm}
        onOpenChange={setShowUpsellingBackConfirm}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              {t("Conferma navigazione")}
            </DialogTitle>
            <DialogDescription className="text-md font-semibold">
              {t(
                "Tornando indietro, i servizi selezionati verranno rimossi dal carrello.",
              )}
              <br />
              {t("Sei sicuro di voler continuare?")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <div className="flex flex-row justify-between w-full">
              <Button
                className="flex-1"
                variant="muted"
                onClick={cancelUpsellingBackNavigation}
              >
                {t("Annulla")}
              </Button>
              <Button
                className="flex-1 ml-4"
                variant="secondary"
                onClick={confirmUpsellingBackNavigation}
              >
                {t("Conferma")}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {!isVoucherBookingWithoutVouchers && (
        <div
          className={cn(
            "fixed inset-0 z-50 lg:hidden transition-all duration-300",
            {
              "pointer-events-none": !mobileCartOpen,
              "pointer-events-all": mobileCartOpen,
            },
          )}
        >
          <div
            className={cn(
              "absolute inset-0 bg-black transition-opacity duration-300",
              {
                "opacity-0": !mobileCartOpen,
                "opacity-50": mobileCartOpen,
              },
            )}
            onClick={() => {
              setMobileCartOpenLocal(false);
              setMobileCartOpen(false);
            }}
          />
          <div
            className={cn(
              "absolute right-0 top-0 h-full w-full max-w-sm bg-white shadow-xl transition-transform duration-300 transform",
              {
                "translate-x-full": !mobileCartOpen,
                "translate-x-0": mobileCartOpen,
              },
            )}
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-4 border-b">
                <h4 className="text-lg font-semibold">
                  {t("Riepilogo prenotazione")}
                </h4>
                <button
                  onClick={() => {
                    setMobileCartOpenLocal(false);
                    setMobileCartOpen(false);
                  }}
                  className="p-2 rounded-full hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <BookingCartSidebar
                  onRemoveItem={handleRemoveFromCart}
                  loadingRemove={removeFromCartFetcher.state !== "idle"}
                  loadingAdd={
                    loadingAddToCart ||
                    addToCartUpsellingFetcher.state === "submitting"
                  }
                  hasAddedToCartInThisFlow={hasAddedToCartInThisFlow}
                  hideRemoveButtons={currentStep === 8}
                  hideTitle={true}
                  isVoucherBooking={isVoucherBooking}
                  selectedDate={selectedDate}
                  guestsCount={guestsCount}
                  onCompleteBooking={() => {
                    setMobileCartOpenLocal(false);
                    setMobileCartOpen(false);
                    goToStep7();
                  }}
                  showCompleteBookingButton={currentStep === 6}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {(isInitializingSession ||
        loadingAddToCart ||
        addToCartUpsellingFetcher.state === "submitting" ||
        removeFromCartFetcher.state === "submitting") && <GlobalLoader />}
    </div>
  );
}
